/**
 * SEO Component — Direct DOM manipulation for guaranteed meta tag override.
 * Bypasses react-helmet-async issues by directly updating document head.
 * Falls back gracefully and cleans up on unmount.
 */
import { useEffect } from 'react';

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  el.setAttribute('data-seo', 'true');
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  el.setAttribute('data-seo', 'true');
}

// Hreflang alternates — appended (cleaned up between renders to avoid duplicates).
function setHreflang(canonical, hreflangEs) {
  // Remove any prior SEO-managed hreflang alternates
  document.querySelectorAll('link[data-seo-hreflang="1"]').forEach((el) => el.remove());
  if (!hreflangEs) return;
  const pairs = [
    { hreflang: 'en-US', href: canonical },
    { hreflang: 'es', href: hreflangEs },
    { hreflang: 'es-US', href: hreflangEs },
    { hreflang: 'x-default', href: canonical },
  ];
  pairs.forEach((p) => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', p.hreflang);
    el.setAttribute('href', p.href);
    el.setAttribute('data-seo', 'true');
    el.setAttribute('data-seo-hreflang', '1');
    document.head.appendChild(el);
  });
}

function setSchema(nodes) {
  // Remove any previous SEO-managed schema
  document.querySelectorAll('script[data-seo-schema]').forEach(s => s.remove());
  if (!nodes || !nodes.length) return;
  // Dedupe by @id (fallback @type); later (page-specific) nodes win over the
  // generic defaults, so a page that ships its own Organization/WebPage isn't
  // duplicated by the stacked default.
  const map = new Map();
  const order = [];
  nodes.forEach((n) => {
    if (!n || !n['@type']) return;
    const key = n['@id'] || n['@type'];
    if (!map.has(key)) order.push(key);
    map.set(key, n);
  });
  const deduped = order.map((k) => map.get(k));
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-schema', 'true');
  // Emit a single connected @graph (Google-recommended for stacked entities)
  script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": deduped });
  document.head.appendChild(script);
}

// Build a BreadcrumbList from the canonical path (Home > seg > seg ...).
function buildBreadcrumb(canonical) {
  let path = '/';
  try { path = new URL(canonical).pathname; } catch (e) {}
  const parts = path.split('/').filter(Boolean);
  const items = [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://web-techservices.com/" }];
  let acc = "https://web-techservices.com";
  parts.forEach((seg, i) => {
    acc += "/" + seg;
    const name = decodeURIComponent(seg).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    items.push({ "@type": "ListItem", "position": i + 2, "name": name, "item": acc });
  });
  return { "@type": "BreadcrumbList", "@id": canonical + "#breadcrumb", "itemListElement": items };
}

// Stacked default graph: Organization + WebSite + WebPage + BreadcrumbList.
// Every <SEO> page gets this rich structured data automatically; pages may add
// their own nodes (Service, FAQPage, Article, etc.) which get stacked in too.
export function buildDefaultGraph(title, description, canonical, ogImage) {
  const org = {
    "@type": "Organization",
    "@id": "https://web-techservices.com/#organization",
    "name": "Web-TechServices",
    "url": "https://web-techservices.com/",
    "logo": { "@type": "ImageObject", "url": "https://web-techservices.com/logo.png" },
    "sameAs": [
      "https://www.facebook.com/webtechservices",
      "https://www.linkedin.com/company/web-techservices",
      "https://twitter.com/webtechservices"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-817-264-7476",
      "contactType": "customer service",
      "areaServed": "US",
      "availableLanguage": "English"
    }
  };
  const website = {
    "@type": "WebSite",
    "@id": "https://web-techservices.com/#website",
    "url": "https://web-techservices.com/",
    "name": "Web-TechServices",
    "publisher": { "@id": "https://web-techservices.com/#organization" },
    "inLanguage": "en-US"
  };
  const webpage = {
    "@type": "WebPage",
    "@id": canonical + "#webpage",
    "url": canonical,
    "name": title,
    "description": description,
    "isPartOf": { "@id": "https://web-techservices.com/#website" },
    "about": { "@id": "https://web-techservices.com/#organization" },
    "primaryImageOfPage": ogImage ? { "@type": "ImageObject", "url": ogImage } : undefined,
    "breadcrumb": { "@id": canonical + "#breadcrumb" },
    "inLanguage": "en-US"
  };
  return [org, website, webpage, buildBreadcrumb(canonical)];
}

const SEO = ({ 
  title = "Web-TechServices | AI-Powered Website Analysis & Development",
  description = "Transform your business with AI-powered website analysis, development, and optimization. Get instant insights on performance, SEO, conversions, and Core Web Vitals.",
  keywords = "website analyzer, AI web development, SEO optimization, performance audit, conversion optimization",
  canonical = null,
  ogImage = "https://static.prod-images.emergentagent.com/jobs/3ffa77a1-0bda-46eb-9179-0a59cf5bf2e9/images/6a98610b6231c606fb2427cf455bc688e724a3a8c86b57edf4b5d30439b13924.png",
  ogType = "website",
  noindex = false,
  schema = null,
  hreflangEs = null,
}) => {
  useEffect(() => {
    // Self-referencing canonical when the page didn't pass one explicitly.
    // Previously this defaulted to the HOMEPAGE, so every <SEO> page without an
    // explicit canonical told Google it was a duplicate of "/" — the #1 driver
    // of the sitewide GSC "Duplicate, Google chose different canonical" errors.
    const _path = (window.location.pathname || '/').replace(/\/+$/, '');
    const resolvedCanonical = canonical || ('https://web-techservices.com' + (_path || '/'));

    // Title
    document.title = title;

    // Standard meta
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Canonical
    setLink('canonical', resolvedCanonical);

    // OG tags
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', resolvedCanonical);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:site_name', 'Web-TechServices.com');

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:url', resolvedCanonical);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    // Geo
    setMeta('name', 'geo.region', 'US-TX');
    setMeta('name', 'geo.placename', 'Arlington, Texas');
    setMeta('name', 'geo.position', '32.7357;-97.1081');
    setMeta('name', 'ICBM', '32.7357, -97.1081');

    // Schema — stacked @graph: Organization + WebSite + WebPage + Breadcrumbs,
    // plus any page-specific schema (single object or array), always indexable.
    const extra = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    const extraNodes = [];
    extra.filter(Boolean).forEach((n) => {
      const { ['@context']: _c, ...rest } = n;
      if (Array.isArray(rest['@graph'])) {
        // Page passed a full schema doc — flatten its @graph nodes into ours.
        rest['@graph'].forEach((g) => {
          if (g && g['@type']) { const { ['@context']: _c2, ...gr } = g; extraNodes.push(gr); }
        });
      } else if (rest['@type']) {
        extraNodes.push(rest);
      }
    });
    setSchema(buildDefaultGraph(title, description, resolvedCanonical, ogImage).concat(extraNodes));

    // Hreflang alternates (only when ES counterpart provided)
    setHreflang(resolvedCanonical, hreflangEs);

    return () => {
      // On unmount, drop this page's stacked schema so a following page that
      // manages its own meta (e.g. via react-helmet) gets a clean slate for the
      // global StructuredData fallback to populate.
      document.querySelectorAll('script[data-seo-schema]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, ogImage, ogType, noindex, schema, hreflangEs]);

  return null;
};

export default SEO;
